<script>
import { mapActions, mapGetters } from 'vuex';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'VoiceCallModal',

  components: {
    Icon,
  },

  data() {
    return {
      callStartTime: null,
      callDuration: '00:00',
      durationInterval: null,
      position: { x: 0, y: 0 },
      dragging: false,
      dragOffset: { x: 0, y: 0 },
      modalBounds: { width: 0, height: 0 },
    };
  },

  computed: {
    ...mapGetters({
      incomingCall: 'voiceCall/getCurrentCall',
      showModal: 'voiceCall/showVoiceCallModal',
      isConfirmed: 'voiceCall/getIsConfirmed',
      globalConfig: 'globalConfig/get',
    }),

    isIncomingCall() {
      return this.incomingCall?.direction === 'incoming';
    },

    isOutgoingCall() {
      return this.incomingCall?.direction === 'outgoing';
    },

    modalTitle() {
      if (this.isConfirmed) {
        return this.$t('VOICE_CALL.MODAL.CALL_IN_PROGRESS_TITLE');
      }

      if (this.isOutgoingCall) {
        return this.$t('VOICE_CALL.MODAL.CALLING_TITLE');
      }

      return this.$t('VOICE_CALL.MODAL.INCOMING_CALL_TITLE');
    },

    contactNumber() {
      if (this.isIncomingCall) {
        return this.incomingCall?.from;
      }

      if (this.isOutgoingCall) {
        return this.incomingCall?.to;
      }

      return null;
    },

    callerNumber() {
      const number = this.contactNumber;
      return number || this.$t('VOICE_CALL.MODAL.NUMBER_UNAVAILABLE');
    },

    contactText() {
      return this.$t('VOICE_CALL.MODAL.CONTACT');
    },

    showAcceptRejectButtons() {
      return this.isIncomingCall && !this.isConfirmed;
    },

    showEndCallButton() {
      return this.isConfirmed || this.isOutgoingCall;
    },

    formattedTime() {
      return this.callDuration;
    },

    modalStyle() {
      return {
        transform: `translate(${this.position.x}px, ${this.position.y}px)`,
        cursor: this.dragging ? 'grabbing' : 'default',
      };
    },
  },

  watch: {
    isConfirmed(newVal) {
      if (newVal) {
        this.startCallTimer();
      } else {
        this.stopCallTimer();
      }
    },

    showModal(newVal) {
      if (!newVal) {
        this.resetModal();
      }
    },
  },

  mounted() {
    this.centerModal();
    this.calculateModalBounds();
  },

  beforeUnmount() {
    this.stopCallTimer();
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  },

  methods: {
    ...mapActions({
      acceptIncomingCall: 'voiceCall/acceptIncomingCall',
      rejectIncomingCall: 'voiceCall/rejectIncomingCall',
      endCallService: 'voiceCall/endCall',
    }),

    calculateModalBounds() {
      this.$nextTick(() => {
        const modal = this.$el?.querySelector('.voice-call-modal');
        if (modal) {
          const rect = modal.getBoundingClientRect();
          this.modalBounds.width = rect.width;
          this.modalBounds.height = rect.height;
        }
      });
    },

    centerModal() {
      this.$nextTick(() => {
        const modal = this.$el?.querySelector('.voice-call-modal');
        if (modal) {
          const rect = modal.getBoundingClientRect();
          this.position.x = (window.innerWidth - rect.width) / 2;
          this.position.y = (window.innerHeight - rect.height) / 2;
          this.modalBounds.width = rect.width;
          this.modalBounds.height = rect.height;
        }
      });
    },

    async acceptCall() {
      try {
        await this.acceptIncomingCall();
      } catch (error) {
        useAlert(this.$t('VOICE_CALL.API.ERRORS.ACCEPTING_CALL'));
      }
    },

    async rejectCall() {
      try {
        await this.rejectIncomingCall();
      } catch (error) {
        useAlert(this.$t('VOICE_CALL.API.ERRORS.REJECTING_CALL'));
      }
    },

    async endCall() {
      try {
        await this.endCallService();
      } catch (error) {
        useAlert(this.$t('VOICE_CALL.API.ERRORS.ENDING_CALL'));
      }
    },

    startCallTimer() {
      if (this.durationInterval) {
        clearInterval(this.durationInterval);
      }

      this.callStartTime = new Date();
      this.durationInterval = setInterval(() => {
        const now = new Date();
        const diff = Math.floor((now - this.callStartTime) / 1000);
        const minutes = Math.floor(diff / 60)
          .toString()
          .padStart(2, '0');
        const seconds = (diff % 60).toString().padStart(2, '0');
        this.callDuration = `${minutes}:${seconds}`;
      }, 1000);
    },

    stopCallTimer() {
      if (this.durationInterval) {
        clearInterval(this.durationInterval);
        this.durationInterval = null;
      }
      this.callDuration = '00:00';
    },

    resetModal() {
      this.stopCallTimer();
      this.callStartTime = null;
    },

    startDrag(e) {
      if (!e.target.closest('.modal-header')) return;

      this.dragging = true;
      this.dragOffset.x = e.clientX - this.position.x;
      this.dragOffset.y = e.clientY - this.position.y;

      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);

      e.preventDefault();
    },

    onDrag(e) {
      if (!this.dragging) return;

      let newX = e.clientX - this.dragOffset.x;
      let newY = e.clientY - this.dragOffset.y;

      newX = Math.max(
        0,
        Math.min(newX, window.innerWidth - this.modalBounds.width)
      );
      newY = Math.max(
        0,
        Math.min(newY, window.innerHeight - this.modalBounds.height)
      );

      this.position.x = newX;
      this.position.y = newY;
    },

    stopDrag() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    searchContact() {
      const currentId = this.$route.params.id;
      let phoneNumber = this.contactNumber ?? '';
      phoneNumber = phoneNumber.replace(/^[+0]+/, '');

      this.$router.push({
        name: 'contacts_dashboard_index',
        params: {
          id: currentId,
        },
        query: {
          page: 1,
          search: encodeURIComponent(phoneNumber),
        },
      });
    },
  },
};
</script>

<template>
  <div class="fixed inset-0 z-[1000] voice-call-overlay">
    <div
      class="absolute top-0 left-0 bg-n-background rounded-3xl p-4 w-[260px] shadow-2xl voice-call-modal"
      :style="modalStyle"
      @mousedown="startDrag"
    >
      <div
        class="p-0 rounded-full border border-n-brand-opposite my-4 modal-header"
        :style="{ cursor: dragging ? 'grabbing' : 'grab' }"
      >
        <h3
          class="p-0 mx-2 text-n-brand-opposite text-center text-md font-medium"
        >
          {{ modalTitle }}
        </h3>
      </div>

      <div class="modal-body">
        <div class="flex flex-col items-center mb-4 gap-2">
          <div class="mx-auto">
            <img
              :src="globalConfig.logoThumbnail"
              :alt="globalConfig.installationName"
              class="block object-contain w-auto h-16 mx-auto"
            />
          </div>
          <div class="flex-1 text-center caller-details">
            <p class="m-0 mb-1 text-n-brand-opposite text-base">
              {{ callerNumber }}
            </p>
          </div>
          <div
            v-if="contactNumber"
            class="cursor-pointer rounded-full bg-n-brand-opposite"
          >
            <button class="text-white dark:text-black" @click="searchContact">
              {{ contactText }}
            </button>
          </div>
        </div>

        <div class="flex justify-evenly items-center w-full">
          <div v-if="showEndCallButton" class="flex justify-center w-full">
            <button
              class="h-20 w-20 border-none rounded-full cursor-pointer items-center transition-all duration-200 ease-in-out bg-red-600 text-white btn-end-call disabled:opacity-60 disabled:cursor-not-allowed"
              @click="endCall"
            >
              <Icon
                icon="i-lucide-phone-off"
                class="size-8 text-white flex-shrink-0"
              />
            </button>
          </div>

          <div
            v-else-if="showAcceptRejectButtons"
            class="flex justify-evenly w-full"
          >
            <button
              class="h-20 w-20 border-none rounded-full cursor-pointer items-center transition-all duration-200 ease-in-out bg-green-600 text-white btn-accept disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="isProcessing"
              @click="acceptCall"
            >
              <Icon
                icon="i-lucide-phone"
                class="size-8 text-white flex-shrink-0"
              />
            </button>
            <button
              class="h-16 w-16 border-none rounded-full cursor-pointer items-center transition-all duration-200 ease-in-out bg-n-brand-opposite text-white btn-reject disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="isProcessing"
              @click="rejectCall"
            >
              <Icon
                icon="i-lucide-phone-off"
                class="size-8 text-white flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4 text-center modal-footer">
        <p class="m-0 text-gray-600 text-sm font-bold call-timer">
          {{ isProcessing ? formattedTime : '' }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.voice-call-overlay {
  pointer-events: none;
}

.voice-call-modal {
  pointer-events: all;
  user-select: none;
}

.btn-accept:hover:not(:disabled),
.btn-reject:hover:not(:disabled),
.btn-end-call:hover:not(:disabled) {
  transform: translateY(-2px);
  opacity: 0.9;
}

.is-calling .modal-header h3 {
  animation: pulse 2s infinite;
}
</style>
